/**
 * Hook for fetching relationship graph
 * 
 * Returns relationship graph with materialized connection groups.
 * This is the new graph-driven approach that replaces hardcoded relationship logic.
 */

import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { erp } from '@/lib/erp';
import type { RelationshipGraphResponse } from '@/lib/erp/docs';

/**
 * Hook to fetch relationship graph for a given doctype and name.
 * 
 * @param doctype - Source document type (e.g., "Item")
 * @param docname - Source document name (e.g., "ITEM-001")
 * @param options - Optional configuration
 * @returns React Query result with relationship graph data
 */
export function useRelationshipGraph(
  doctype: string,
  docname?: string,
  options?: { enabled?: boolean; debug?: boolean }
): UseQueryResult<RelationshipGraphResponse | null, unknown> {
  return useQuery<RelationshipGraphResponse | null>({
    queryKey: ['relationship-graph', doctype, docname], // Must include both
    queryFn: async () => {
      if (!docname) {
        return null;
      }
      
      try {
        return await erp.docs.getRelationshipGraph(doctype, docname, options?.debug);
      } catch (error) {
        console.error('Failed to fetch relationship graph:', error);
        throw error;
      }
    },
    enabled: options?.enabled !== false && !!doctype && !!docname && docname !== "new", // Both required, block "new"
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Query hygiene: prevent background refetch churn
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnReconnect: false, // Don't refetch on network reconnect
    retry: false, // Don't retry failed requests (fail fast for allowlist-filtered)
  });
}

